import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { apiBaseUrl, prepareAuthHeaders, resolveApiUrl } from "./apiBase";

const unwrap = (response) => (response?.success ? response.data : response ?? null);

/** Query string from defined, non-empty params only (stable order for caching). */
export const toAnalyticsQuery = (params = {}) => {
  const search = new URLSearchParams();
  Object.keys(params)
    .sort()
    .forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== "") {
        search.set(key, String(value));
      }
    });
  const text = search.toString();
  return text ? `?${text}` : "";
};

const biQuery = (path) => (params) => `analytics/bi/${path}${toAnalyticsQuery(params)}`;

export const analyticsApi = createApi({
  reducerPath: "analyticsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl,
    prepareHeaders: (headers) =>
      prepareAuthHeaders(headers, {
        acceptJson: true,
        contentTypeJson: true,
      }),
  }),
  keepUnusedDataFor: 120,
  endpoints: (builder) => ({
    fetchDashboardSales: builder.query({
      query: ({ dateFrom, dateTo }) => {
        const params = new URLSearchParams({ dateFrom, dateTo });
        return `analytics/dashboard-sales?${params.toString()}`;
      },
      transformResponse: unwrap,
    }),
    fetchMetaDashboard: builder.query({ query: (params) => `analytics/meta/dashboard${toAnalyticsQuery(params)}`, transformResponse: unwrap, keepUnusedDataFor: 0 }),
    fetchMetaHierarchy: builder.query({ query: (params) => `analytics/meta/hierarchy${toAnalyticsQuery(params)}`, transformResponse: unwrap, keepUnusedDataFor: 0 }),
    fetchMetaComparison: builder.query({ query: (params) => `analytics/meta/comparison${toAnalyticsQuery(params)}`, transformResponse: unwrap, keepUnusedDataFor: 0 }),
    fetchTrafficDashboard: builder.query({ query: (params) => `analytics/traffic/dashboard${toAnalyticsQuery(params)}`, transformResponse: unwrap, keepUnusedDataFor: 0 }),
    fetchBiOverview: builder.query({ query: biQuery("overview"), transformResponse: unwrap }),
    fetchBiSales: builder.query({ query: biQuery("sales"), transformResponse: unwrap }),
    fetchBiTransactions: builder.query({ query: biQuery("sales/transactions"), transformResponse: unwrap }),
    fetchBiProducts: builder.query({ query: biQuery("products"), transformResponse: unwrap }),
    fetchBiBreakdowns: builder.query({ query: biQuery("products/breakdowns"), transformResponse: unwrap }),
    fetchBiVariants: builder.query({ query: biQuery("products/variants"), transformResponse: unwrap }),
    fetchBiInventory: builder.query({ query: biQuery("inventory"), transformResponse: unwrap }),
    fetchBiInventoryVariants: builder.query({ query: biQuery("inventory/variants"), transformResponse: unwrap }),
    fetchBiProfitability: builder.query({ query: biQuery("profitability"), transformResponse: unwrap }),
    fetchBiInsights: builder.query({ query: biQuery("insights"), transformResponse: unwrap }),
    fetchBiMonthly: builder.query({ query: biQuery("reports/monthly"), transformResponse: unwrap }),
  }),
});

export const {
  useFetchDashboardSalesQuery,
  useFetchTrafficDashboardQuery,
  useFetchMetaDashboardQuery,
  useFetchMetaHierarchyQuery,
  useFetchMetaComparisonQuery,
  useFetchBiOverviewQuery,
  useFetchBiSalesQuery,
  useFetchBiTransactionsQuery,
  useFetchBiProductsQuery,
  useFetchBiBreakdownsQuery,
  useFetchBiVariantsQuery,
  useFetchBiInventoryQuery,
  useFetchBiInventoryVariantsQuery,
  useFetchBiProfitabilityQuery,
  useFetchBiInsightsQuery,
  useFetchBiMonthlyQuery,
} = analyticsApi;

const filenameFromDisposition = (header, fallback) => {
  const match = /filename="?([^";]+)"?/i.exec(header || "");
  return match ? match[1] : fallback;
};

/**
 * Downloads a server-generated CSV. The server applies the same validation,
 * company scope and cost visibility as the dashboard; nothing is built client-side.
 */
export const downloadAnalyticsCsv = async (dataset, params = {}) => {
  const headers = prepareAuthHeaders(new Headers(), {});
  const response = await fetch(
    resolveApiUrl(`/analytics/bi/exports/${dataset}${toAnalyticsQuery(params)}`),
    { headers }
  );
  if (!response.ok) {
    const error = new Error(`Export failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  const blob = await response.blob();
  const filename = filenameFromDisposition(
    response.headers.get("content-disposition"),
    `black-white-${dataset}.csv`
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return filename;
};
